import {
  CARDS_PER_HAND,
  isRedealFrame,
  pointsInCards,
  TRICK_POINTS_EACH,
  WIDOW_SIZE,
  type ShelemOutcome,
  type ShelemView,
  type TrickPlay,
} from "@bing/engine-shelem";
import { useRef } from "react";
import type { SfxName } from "../sound/sfx";
import { CARD_ENTRY_MS } from "./card-motion";
import {
  HAND_END_CARD_MS,
  KAF_DISCARD_SWEEP_MS,
  KAF_PICKUP_LANDS_MS,
  KAF_PICKUP_NOTICE_MS,
  LEAD_LAND_MS,
  REDEAL_ENDS_MS,
  REDEAL_PASS_MS,
  TRUMP_ANNOUNCE_ENDS_MS,
} from "./shelem-beats";
import { useTransientNotice } from "./table-notice";
import { useEventSfx } from "./use-table-sfx";
import { TRICK_COLLECT_MS, TRICK_HOLD_MS, type TrickHold } from "./use-trick-hold";

/* ------------------------------------------------------------------ *
 * شلم's MOMENTS (P1 Task 5) — the کف taken and laid down, and the trump named by the حاکم's first
 * card — as the table plays them, off the frames alone (primitive keys: a socket-frame effect never
 * depends on an object off the frame). Each moment has a KEY; a moment the table SAW arrive (the
 * frame before it was on this mount) plays, and a moment the table MOUNTED on (a resume, a
 * reconnect, the layout harness) is adopted in silence: its state is drawn, its animation and its
 * sound are not replayed. The lengths are `shelem-beats.ts`'s, which the engine's beats cover.
 * ------------------------------------------------------------------ */

/** A deal's identity: a played hand moves the count, a redeal the dealer. */
export const handKeyOf = (view: ShelemView): string => `${view.playedHands}:${view.dealer}`;

/**
 * `key` while it is LIVE — it arrived on this mount (the table drew the frame before it) — and ""
 * when the table mounted on it, or it is empty. Read during render off a ref, so the very first
 * frame of a live moment already draws it (never a flash of the settled state before it moves).
 */
export function useLiveKey(key: string): string {
  const seen = useRef<{ key: string; live: boolean } | null>(null);
  if (seen.current === null) seen.current = { key, live: false };
  else if (seen.current.key !== key) seen.current = { key, live: key !== "" };
  return seen.current.live ? key : "";
}

export interface KafMoments {
  /** The four کف cards in YOUR sixteen while you lay four down — the hand's LAST four (the engine's
   *  order, pinned by its `pacing.test.ts`); empty for every other seat and phase. */
  readonly kaf: readonly string[];
  /** The live pickup: the four cards are still flying from the felt to the حاکم. */
  readonly flying: boolean;
  /** The other seats' line «{name} زمین را برداشت» is up (from the pickup's frame — a mount on the
   *  کف shows it too: it says what is true). */
  readonly line: boolean;
  /** The live frame after the discard: the four, face down, go to the حاکم's pile. */
  readonly sweeping: boolean;
}

/** The کف's pickup and its laying-down, for whoever reads the table (see above); `widowPickup`
 *  sounds on the live pickup's frame, for every seat — never at a mount. */
export function useKafMoments(view: ShelemView): KafMoments {
  const hand = handKeyOf(view);
  const declaring = view.phase === "declaring" && view.declarer !== null;
  const kafKey = declaring ? `${hand}|${String(view.declarer)}` : "";
  const flying = useTransientNotice(useLiveKey(kafKey), KAF_PICKUP_LANDS_MS);
  const line = useTransientNotice(kafKey, KAF_PICKUP_NOTICE_MS);
  useEventSfx(kafKey, "widowPickup");
  const laid =
    view.phase === "playing" && view.discardCount === WIDOW_SIZE && view.declarer !== null
      ? `${hand}|${String(view.declarer)}`
      : "";
  const sweeping = useTransientNotice(useLiveKey(laid), KAF_DISCARD_SWEEP_MS);
  const kaf =
    declaring && view.declarer === view.you && view.hand.length === CARDS_PER_HAND + WIDOW_SIZE
      ? view.hand.slice(-WIDOW_SIZE)
      : [];
  return { kaf, flying, line, sweeping };
}

export interface TrumpMoments {
  /** The live first card of trick 1 is in flight, or its announce is up: the suit shows NOWHERE but
   *  the announce, and the seat after the حاکم cannot play over it. */
  readonly revealing: boolean;
  /** «حکم: ♠ — {name}» is up — the card has landed. */
  readonly announce: boolean;
}

/** The trump named by the حاکم's first card (D9): the card lands (`LEAD_LAND_MS`, `ThrownCard`'s
 *  flight), then the announce stays `TRUMP_ANNOUNCE_MS`, `trumpCall` sounding as it appears. A
 *  table that mounts mid-trick names trump at once (adopted). */
export function useTrumpMoments(view: ShelemView): TrumpMoments {
  // exactly the engine's announce beat (`trickNumber === 0 && currentTrick.length === 1`): a table
  // that first sees trick 1 with more cards on it missed the moment, and adopts it
  const lead =
    view.phase === "playing" &&
    view.trickNumber === 0 &&
    view.currentTrick.length === 1 &&
    view.trump !== null
      ? view.currentTrick[0]
      : undefined;
  const live = useLiveKey(lead ? `${handKeyOf(view)}|${lead.seat}|${lead.card}` : "");
  const inFlight = useTransientNotice(live, LEAD_LAND_MS);
  const revealing = useTransientNotice(live, TRUMP_ANNOUNCE_ENDS_MS);
  const announce = revealing && !inFlight;
  useEventSfx(announce ? live : "", "trumpCall");
  return { revealing, announce };
}

export interface RedealMoments {
  /** The live redeal's window: the three «پاس» stay on their seats, and the new auction waits —
   *  its row, its centre, the glow on the seat to speak (the engine's redeal beat covers it). */
  readonly holding: boolean;
  /** Its first part — the third «پاس» seen ALONE: your fan still holds the old hand. */
  readonly passing: boolean;
  /** Its second — «سه پاس؛ دست دوباره پخش می‌شه», the new hand dealing in under it. */
  readonly notice: boolean;
  /** The three who passed while the window holds (their «پاس» stays up); `[]` otherwise. */
  readonly passedOut: readonly number[];
}

/**
 * THE REDEAL (D6; the final review B-I2): three opening passes deal the hand again at once — no frame
 * carried the third «پاس», so the redeal's frame carries all three (`passedOut`). LIVE only (the table
 * saw the frame before it): the third «پاس» on its seat for `REDEAL_PASS_MS` (its cue is
 * `useAuctionSfx`'s), then the notice and the `shuffle` as the new hand deals in, to
 * `REDEAL_ENDS_MS`. A table that MOUNTS on a redealt deal (a resume, a reconnect, the layout harness)
 * draws the deal as it is — no notice, no shuffle — like every moment it did not see arrive.
 */
export function useRedealMoments(view: ShelemView): RedealMoments {
  const live = useLiveKey(isRedealFrame(view) ? handKeyOf(view) : "");
  const holding = useTransientNotice(live, REDEAL_ENDS_MS);
  const passing = useTransientNotice(live, REDEAL_PASS_MS);
  const notice = holding && !passing;
  useEventSfx(notice ? live : "", "shuffle");
  return { holding, passing, notice, passedOut: holding ? (view.passedOut ?? []) : [] };
}

/* ------------------------------------------------------------------ *
 * THE END OF A TRICK AND OF A HAND (P1 Task 6) — read off the trick hold (`useTrickHold`, the felt's
 * one truth about what it SHOWS) and the frame's `lastHand`, primitive keys only.
 * ------------------------------------------------------------------ */

/** The held trick's identity («seat:card,…») while it holds; "" otherwise. */
const heldSig = (hold: TrickHold): string =>
  hold.holding ? (hold.shown.completedTrick ?? []).map((p) => `${p.seat}:${p.card}`).join(",") : "";

/** The card points (A/10 = 10, 5 = 5) in a trick — the «+۱۰» chip's number; the trick's own 5 is not
 *  a point card's. */
export const trickCardPoints = (plays: readonly TrickPlay[]): number =>
  pointsInCards(plays.map((p) => p.card));

/**
 * How long the «+۱۰» stays up from its collect's start (P2-1; Task 6 fix 1 — review I-2): the
 * collect's sweep, then the flight of a card led the moment the trick is released — so it is gone as
 * the EARLIEST next lead lands, and never sits over it (a bot's lead lands 150 ms later still). 750 ms:
 * `pop-in-glow` brings it to full opacity in 140, so it is read at full opacity for 610 — riding the
 * collect's fading layer it was ≥ 0.5 opaque for ~75 ms.
 */
export const TRICK_POINTS_MS = TRICK_COLLECT_MS + CARD_ENTRY_MS;

/** The «+۱۰» on the felt — the collected trick's card points and its winner, latched at its collect. */
export interface TrickPoints {
  /** The trick's identity («seat:card,…»): one chip, and one entrance, per trick. */
  readonly key: string;
  readonly points: number;
  /** Its winner — the hold's, read off its cards and frozen (the frames after it move `turn` on). */
  readonly winner: number;
}

/**
 * THE «+۱۰» (P2-1): from the held trick's COLLECT — its `TRICK_HOLD_MS` at rest over, never before: the
 * sweep is what names the taker (spoiler discipline) — for `TRICK_POINTS_MS`, OUTLIVING the collect's
 * own layer, whose fade is the shared hold's (`useTrickHold`, حکم's and بی‌دل's too — untouched).
 * LATCHED when the trick is first held (its cards, points and winner) and TIMED from then, both ends
 * from the same start: so neither the release — the hold's `shown` goes live — nor any newer frame
 * cuts it short or keeps it, and a main thread that runs the collect's start and the release in one
 * batch still shows it (a render that never saw the collect cannot lose it). Once per trick (keyed by
 * its cards), on a mount-held trick too (its collect plays, as always); no point card, no chip.
 */
export function useTrickPoints(hold: TrickHold): TrickPoints | null {
  const sig = heldSig(hold);
  const latched = useRef<TrickPoints | null>(null);
  if (sig !== "" && latched.current?.key !== sig)
    latched.current = {
      key: sig,
      points: trickCardPoints(hold.shown.completedTrick ?? []),
      winner: hold.shown.turn,
    };
  const l = latched.current;
  const key = l !== null && l.points > 0 ? l.key : "";
  const resting = useTransientNotice(key, TRICK_HOLD_MS);
  const alive = useTransientNotice(key, TRICK_HOLD_MS + TRICK_POINTS_MS);
  return key !== "" && alive && !resting ? l : null;
}

/**
 * The contract HUD's «گرفته» per team, AS COLLECTED (P1-6: «updating after each collect»): the engine
 * credits a trick the moment it completes, so while the felt still holds it — its 600 ms at rest and
 * its sweep — the frame's `pointsTaken` is less that trick (its card points + 5) for the team that took
 * it; the HUD moves with the sweep, never before the felt has named the taker. Only a trick in THIS
 * hand's play log is in the frame's count: the carried 12th trick never is — the next deal starts at
 * 0, and a human who calls «شلم» and lays the کف down inside the carried trick's hold (< 950 ms) is
 * already in the next hand's play while it is still collected.
 */
export function pointsCollected(view: ShelemView, hold: TrickHold): readonly number[] {
  const sig = heldSig(hold);
  if (sig === "" || view.phase !== "playing") return view.pointsTaken;
  const inThisHand = (view.playLog ?? []).some(
    (trick) => trick.map((p) => `${p.seat}:${p.card}`).join(",") === sig,
  );
  if (!inThisHand) return view.pointsTaken;
  const team = hold.shown.turn % 2; // the latched winner
  const held = trickCardPoints(hold.shown.completedTrick ?? []) + TRICK_POINTS_EACH;
  return view.pointsTaken.map((p, t) => (t === team ? p - held : p));
}

/** The hand's cue (R5): the contract made; missed — set, یاسا, a failed «شلم» call; all twelve — a
 *  sweep, a «شلم» call made. */
export function handEndCue(outcome: ShelemOutcome): SfxName {
  if (outcome === "made") return "shelemMade";
  if (outcome === "sweep" || outcome === "shelemMade") return "shelemSlam";
  return "shelemSet";
}

/**
 * THE HAND-END WINDOW's key (P1 Task 7): the next deal's FIRST frame — the 12th trick carried on it
 * and `lastHand` the hand just played (the engine's hand-end beat, `HAND_END_BEAT_MS`) — or the
 * match's end; "" for every other frame. The auction's first word sweeps the carried trick, so the
 * window closes the moment the next hand speaks; a redeal carries no trick (and replays nothing).
 */
export function handEndKeyOf(view: ShelemView): string {
  if (view.lastHand === null) return "";
  if (view.phase === "gameOver") return `over:${view.playedHands}`;
  return view.phase === "bidding" && (view.completedTrick ?? []).length > 0
    ? `hand:${view.playedHands}`
    : "";
}

export interface HandEndMoments {
  /** THE HAND-END CARD is up: from the 12th trick's collect, for `HAND_END_CARD_MS` — never at the
   *  match's end (card-98 item 11: the end card's last-hand box says it). */
  readonly card: boolean;
  /** The next auction waits — its bidding row, its centre, the face-down کف, the glow on the seat to
   *  speak — from the hand-end frame until the card has been read (the engine's beat covers it). */
  readonly holding: boolean;
  /** The score box still shows the totals BEFORE the hand — until the card's own entrance: no
   *  hand-end number shows while the 12th trick is still on the felt. */
  readonly priorTotals: boolean;
}

/**
 * THE HAND'S END, once the felt has collected its 12th trick (`feltClear`): the hand-end card for
 * `HAND_END_CARD_MS` — on a mount too (it says what is true; the collect plays at a mount, as always)
 * — and the outcome's cue on a LIVE hand end only (a resume or a reconnect never replays it), never
 * at the match's end (the win / lose chime is the containers' `useOutcomeSfx`, with the end card).
 * The card YIELDS the instant the next auction speaks (its key empties) — and when a turn window of
 * YOURS opens behind it (`turn`, the final review B m7): a table that mounts inside the engine's beat
 * (a reconnect) times its card from the mount, so the server's window — yours, the next auction's
 * opener — could count down behind it with no row; the card, the held auction and the strip's «پایان
 * دست» give way to the countdown. Only to a window other than the one the table held BEFORE this one
 * opened: that one — your own 12th card's, until its `turnEnd` lands (a state frame does not clear it)
 * — ended with the hand, and yielding to it dropped the held HUD and the totals before the hand for
 * that render; a table that MOUNTS on the window held none, so any window of yours is new. Primitive
 * keys only.
 */
export function useHandEndMoments(
  view: ShelemView,
  feltClear: boolean,
  turn: { readonly seat: number; readonly endsAt: number } | null = null,
): HandEndMoments {
  const raw = handEndKeyOf(view);
  // the turn frame the table held BEFORE this window opened — latched during render (as `useLiveKey`
  // is): the last one it drew with no window open, none at all on a mount (a reload whose re-sent
  // window lands before the table — the artwork loads first — yields at once)
  const before = useRef<number | null>(null);
  const opened = useRef<{ key: string; endsAt: number | null } | null>(null);
  if (raw === "") {
    opened.current = null;
    before.current = turn?.endsAt ?? null;
  } else if (opened.current?.key !== raw) opened.current = { key: raw, endsAt: before.current };
  const yourWindow =
    turn !== null && turn.seat === view.you && turn.endsAt !== opened.current?.endsAt;
  const key = yourWindow ? "" : raw;
  // between hands 5.5 s; at the match's end NONE (card-98 item 11, the operator: «No separate card
  // before it») — the end card comes up right after the last collect and says the last hand itself
  // in its own box (`LastHandBox`)
  const card = useTransientNotice(
    key !== "" && feltClear && view.phase !== "gameOver" ? key : "",
    HAND_END_CARD_MS,
  );
  const live = useLiveKey(key);
  const cue = live !== "" && feltClear && view.phase !== "gameOver" ? live : "";
  const last = view.lastHand;
  const sound = last === null ? null : handEndCue(last.outcome);
  useEventSfx(sound === "shelemMade" ? cue : "", "shelemMade");
  useEventSfx(sound === "shelemSet" ? cue : "", "shelemSet");
  useEventSfx(sound === "shelemSlam" ? cue : "", "shelemSlam");
  return {
    card,
    holding: key !== "" && (!feltClear || card),
    priorTotals: key !== "" && !feltClear,
  };
}
